import requester from '@/lib/axios';
import ModalWarning from '@/components/ui/custom/modals/ModalWarning';
import { ACTIVE_LESSON_TAB_STORAGE_KEY } from '@/constants';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import Book from '@/models/Book';
import CoursePath from '@/models/CoursePath';
import Klass from '@/models/Klass';
import { LessonTab } from '@/models/Lesson';
import ScheduledLesson from '@/models/ScheduledLesson';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { updateCoursePath } from '../services/coursePathServices';
import {
  coursePathsQueryKeys,
  lessonBlocksQueryKeys,
  lessonsQueryKeys,
} from '../services/querykeys';
import { ROUTES } from '@/utils/routes';

export type ModeCourseInfo = Pick<
  CoursePath,
  'slug' | 'status' | 'hasLessonBlocks'
>;

export type AllowMode = (
  course: ModeCourseInfo,
  isFirstVersion?: boolean,
) => Promise<void>;
export type MaterialMode = 'upload' | 'default' | 'slides';

export interface ActiveLessonTab {
  previousActiveLessonOrder: number | null;
  activeLessonOrder: number | null;
  activeTab: LessonTab;
}

export interface CourseEditingProps {
  allowBigChanges: boolean;
  activeLessonTab?: ActiveLessonTab;
  handleOpenTab: (isActive: boolean, order: number) => void;
  handleLessonTab: (lessonTab: Partial<ActiveLessonTab>) => void;
  unpublishedCoursesIds: number[];
  getUnpublishedCourses: (ids: number[]) => void;
  setCourseStatus: (status: VersioningStatusEnum) => void;
  allowVersioningMode: AllowMode;
  allowEditingMode: AllowMode;
  courseToPublish(slug: string): Promise<void>;
  book: null | Book;
  setBook: Dispatch<SetStateAction<null | Book>>;
  materialMode: MaterialMode;
  setMaterialMode: Dispatch<SetStateAction<MaterialMode>>;
  reorderScheduledLessons: ScheduledLesson[];
  updateReorderScheduledLessons(scheduledLessons: ScheduledLesson[]): void;
  handleKlassChange(klass: Klass): boolean;
  preventExitWithChanges: (callbackFunc?: () => void) => void;
  invalidateCoursePaths(): Promise<void>;
  invalidateCoursePath(slug: string): Promise<void>;
  changed?: boolean;
  setChanged: Dispatch<SetStateAction<boolean>>;
  hasLessonBlocks: boolean;
  setHasLessonBlocks: Dispatch<SetStateAction<boolean>>;
}

const CourseEditingContext = createContext<CourseEditingProps>(
  {} as CourseEditingProps,
);

export default CourseEditingContext;

export function CourseEditingProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();

  const invalidateCoursePaths = async () =>
    await queryClient.invalidateQueries({
      queryKey: coursePathsQueryKeys.list._def,
    });

  const invalidateCoursePath = async (slug: string) => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: coursePathsQueryKeys.get(slug).queryKey,
      }),

      queryClient.invalidateQueries({
        queryKey: lessonsQueryKeys.nestedList(slug).queryKey,
      }),
    ]);

    await queryClient.invalidateQueries({
      queryKey: lessonBlocksQueryKeys.list(slug).queryKey,
    });

    await queryClient.invalidateQueries({
      queryKey: lessonBlocksQueryKeys.listLessons._def,
    });
  };

  const initialActiveTabState: ActiveLessonTab = {
    previousActiveLessonOrder: null,
    activeLessonOrder: null,
    activeTab: 'details',
  };

  async function courseToPublish(slug: string) {
    const coursePath = await updateCoursePath(slug, {
      status: VersioningStatusEnum.PUBLISHED,
    });

    navigate(`../courses/${coursePath.slug}`, { replace: true });

    await invalidateCoursePath(coursePath.slug);
  }

  const [hasLessonBlocks, setHasLessonBlocks] = useState(false);

  const [allowBigChanges, setAllowBigChanges] = useState(true);
  const isCancelled = useRef<boolean>(false);
  const [activeLessonTab, setActiveLessonTab] = useState<ActiveLessonTab>();
  const [unpublishedCoursesIds, setUnpublishedCoursesIds] = useState<number[]>(
    [],
  );
  const [reorderScheduledLessons, setReorderScheduledLessons] = useState<
    ScheduledLesson[]
  >([]);
  const [newSelectedKlass, setNewSelectedKlass] = useState<Klass>();
  const updateReorderScheduledLessons = (
    scheduledLessons: ScheduledLesson[],
  ) => {
    setReorderScheduledLessons(scheduledLessons);
  };
  const [openChangeKlassModal, setOpenChangeKlassModal] = useState(false);
  const callbackFuncOnConfirmModal = useRef<() => void>();
  const navigate = useNavigate();

  const [book, setBook] = useState<null | Book>(null);
  const [materialMode, setMaterialMode] = useState<MaterialMode>('default');

  const [changed, setChanged] = useState(false);

  const preventExitWithChanges = (callbackFunc?: () => void) => {
    if (!reorderScheduledLessons.length) callbackFunc?.();
    else {
      callbackFuncOnConfirmModal.current = callbackFunc;
      setOpenChangeKlassModal(true);
    }
  };

  function handleLessonTab(lessonTab: Partial<ActiveLessonTab>) {
    setActiveLessonTab(lt => {
      return lt
        ? {
            ...lt,
            ...lessonTab,
          }
        : { ...initialActiveTabState, ...lessonTab };
    });
  }
  const getUnpublishedCourses = useCallback((ids: number[]) => {
    setUnpublishedCoursesIds(ids);
  }, []);

  const allowVersioningMode: AllowMode = async function (
    { slug, status, hasLessonBlocks },
    isFirstVersion = false,
  ) {
    const isCoursePublished = status === VersioningStatusEnum.PUBLISHED;
    const isCourseBeta = status === VersioningStatusEnum.BETA;
    setActiveLessonTab(initialActiveTabState);
    if (isCoursePublished || isCourseBeta) {
      const { data: newCourse } = await requester().patch<CoursePath>(
        `courses/${slug}/`,
        {
          status: VersioningStatusEnum.VERSIONING,
        },
      );
      isFirstVersion = newCourse.version <= 1.0 && isFirstVersion;
      slug = newCourse.slug || slug;
    }
    await invalidateCoursePath(slug);
    return goToVersioningPage(slug, isFirstVersion, hasLessonBlocks);
  };

  function goToEditingPage(slug: string, hasLessonBlocks: boolean = false) {
    if (hasLessonBlocks) {
      return navigate(`/courses/box/${slug}/`);
    }
    return navigate(`/admin/courses/${slug}/lessons/editing`);
  }

  function goToVersioningPage(
    slug: string,
    isFirstVersion: boolean = false,
    hasLessonBlocks: boolean = false,
  ) {
    if (hasLessonBlocks) {
      return navigate(`/courses/box/${slug}/`);
    }
    const isDevelopment =
      import.meta.env.MODE === 'development' ||
      import.meta.env.VITE_DEVELOPMENT_MODE === 'true';

    if (isDevelopment) {
      return navigate(ROUTES.COURSES.STUDIO.INFO(slug));
    }
    return navigate(
      `/admin/courses/${slug}/lessons/${
        isFirstVersion ? 'draft' : 'versioning'
      }`,
    );
  }

  const allowEditingMode: AllowMode = async ({
    status,
    slug,
    hasLessonBlocks,
  }) => {
    setActiveLessonTab(initialActiveTabState);
    if (status === VersioningStatusEnum.PUBLISHED) {
      const { data: newCourse } = await requester().patch<CoursePath>(
        `courses/${slug}/`,
        {
          status: VersioningStatusEnum.EDITING,
        },
      );
      slug = newCourse.slug;
    }
    return goToEditingPage(slug, hasLessonBlocks);
  };

  const handleOpenTab = (isActive: boolean, order: number) => {
    if (isActive) {
      handleLessonTab({
        previousActiveLessonOrder: order,
        activeLessonOrder: null,
      });
    } else {
      handleLessonTab({
        activeLessonOrder: order,
        activeTab:
          activeLessonTab?.previousActiveLessonOrder === order
            ? activeLessonTab?.activeTab
            : 'details',
        previousActiveLessonOrder: null,
      });
    }
  };

  function setCourseStatus(status: VersioningStatusEnum) {
    setAllowBigChanges(status === VersioningStatusEnum.VERSIONING);
  }

  function getSessionStorage(): ActiveLessonTab | null {
    const sessionJson = sessionStorage.getItem(ACTIVE_LESSON_TAB_STORAGE_KEY);
    if (!sessionJson) {
      return null;
    }
    const session = JSON.parse(sessionJson) as ActiveLessonTab;
    return session;
  }
  const handleKlassChange = (klass: Klass) => {
    if (reorderScheduledLessons.length) {
      setNewSelectedKlass(klass);
      setOpenChangeKlassModal(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    let isCancelled = false;
    function saveSessionStorage(name: string, data?: ActiveLessonTab) {
      if (data) {
        sessionStorage.setItem(name, JSON.stringify(data));
      }
    }

    if (!isCancelled) {
      saveSessionStorage(ACTIVE_LESSON_TAB_STORAGE_KEY, activeLessonTab);
    }

    return () => {
      isCancelled = true;
    };
  }, [activeLessonTab]);

  useEffect(() => {
    if (!isCancelled.current) {
      const storage = getSessionStorage();
      storage !== null && setActiveLessonTab(storage);
    }
    return () => {
      isCancelled.current = true;
    };
  }, []);

  useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      e.returnValue = '';
    };
    if (reorderScheduledLessons.length) {
      window.addEventListener('beforeunload', beforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [reorderScheduledLessons]);

  const onCancelChangeKlass = () => setOpenChangeKlassModal(false);

  const onConfirmChangeKlass = () => {
    if (callbackFuncOnConfirmModal.current) {
      callbackFuncOnConfirmModal.current();
    } else {
      navigate(
        `../courses/${newSelectedKlass?.coursePathSlug}/class/${newSelectedKlass?.id}`,
      );
    }
    setOpenChangeKlassModal(false);
    setReorderScheduledLessons([]);
    setChanged(false);
  };

  return (
    <CourseEditingContext.Provider
      value={{
        materialMode,
        setMaterialMode,
        book,
        setBook,
        allowBigChanges,
        activeLessonTab,
        unpublishedCoursesIds,
        handleOpenTab,
        handleLessonTab,
        getUnpublishedCourses,
        setCourseStatus,
        allowVersioningMode,
        allowEditingMode,
        courseToPublish,
        reorderScheduledLessons,
        updateReorderScheduledLessons,
        handleKlassChange,
        preventExitWithChanges,
        invalidateCoursePaths,
        invalidateCoursePath,
        changed,
        setChanged,
        hasLessonBlocks,
        setHasLessonBlocks,
      }}
    >
      {children}
      <ModalWarning
        onClickCancel={onCancelChangeKlass}
        onClickConfirm={onConfirmChangeKlass}
        translationString="modalChangeKlass"
        visible={openChangeKlassModal}
      />
    </CourseEditingContext.Provider>
  );
}
